// ============================================================================
// nevPotlas.js — HIÁNYZÓ NYELVI NEVEK TÖMEGES PÓTLÁSA (2026-10-20, v123; Tamás kérése: "egy gombbal pótolja minden szoba és címke hiányzó nyelvű nevét,
// az admin átnézi, mielőtt ment" - a "Kawakami Mataji" jellegű, lefordítatlan szobanevek ellen).
//
// Három lépés, mind tiszta függvény (a hálózatot és az időt a hívó adja, így tesztelhető):
//   1. hianyzoNevek(szobak, elo, nyelvek):  mely szobának / címkének melyik nyelvi neve hiányzik;
//   2. javaslatokKeszitese(feladatok, hivas, opciok):  egyenként, a szerver percenkénti keretét tartva (alap 4,5 mp köz), megszakítható, részleges;
//   3. mentesiTervek(...):  a MÉG ÜRES nyelvekre beírandó nevek (a javaslat közben beírt nevet nem írja felül).
// A javaslat CSAK javaslat: a mentés külön, az admin jóváhagyásával történik (main.js).
// ============================================================================

/** Hány ms-ot várunk két hívás között: a szerver percenként 15 javaslatot enged (suggestRoomNames), 4,5 mp ~ 13/perc. */
export const HIVAS_KOZ_MS = 4500;
export const TULTERHELES_VARAKOZAS_MS = 20000;

const uresE = (v) => typeof v !== "string" || v.trim() === "";

/**
 * JÁTSZÓTÉR-szobák (2026-10-20, Tamás: "a teszt szobát nem kell fordítani, hibalistán se legyen, az játszótér a fejlesztéshez"): a nevük "teszt..." / "test..." kezdetű
 * (pl. "Szoba teszteléshez" főszoba és alatta a "Teszt-alszoba"), és MINDEN alszobájuk. Ezekre nincs javaslat, és nem számítanak hiányosnak.
 */
const JATSZOTER_NEV = /^(teszt|test)|tesztel|tesztszoba/i;
export function jatszoterE(szoba, szobak) {
  const lista = Array.isArray(szobak) ? szobak : [];
  const nevOf = (r) => [r && r.name, ...Object.values((r && r.localizedNames) || {})].filter((x) => typeof x === "string");
  let r = szoba;
  for (let lepes = 0; r && lepes < 20; lepes++) {
    if (nevOf(r).some((n) => JATSZOTER_NEV.test(n.trim()))) return true;
    r = r.parentRoomId ? lista.find((x) => x.roomId === r.parentRoomId) : null;
  }
  return false;
}

/**
 * @param {Array} szobak  az admin szobalistája ({roomId, name, localizedNames, parentRoomId, archived, deleting})
 * @param {Array} elo     az élő szobalista (a címkekatalógushoz: {roomId, tagCatalog: [{id, group, name, names}]})
 * @param {string[]} nyelvek a csevegési nyelvek kódjai
 * @returns {{szobak: Array, cimkek: Array}}
 */
export function hianyzoNevek(szobak, elo, nyelvek) {
  const kodok = Array.isArray(nyelvek) ? nyelvek : [];
  const kimenet = { szobak: [], cimkek: [] };
  for (const r of Array.isArray(szobak) ? szobak : []) {
    if (!r || !r.roomId || r.deleting === true || r.archived === true || uresE(r.name) || jatszoterE(r, szobak)) continue;
    const nevek = r.localizedNames && typeof r.localizedNames === "object" ? r.localizedNames : {};
    const hianyzo = kodok.filter((k) => uresE(nevek[k]));
    if (hianyzo.length > 0) kimenet.szobak.push({ roomId: r.roomId, name: r.name, hianyzo });
  }
  const nevOf = new Map((Array.isArray(szobak) ? szobak : []).map((r) => [r.roomId, r.name]));
  for (const r of Array.isArray(elo) ? elo : []) {
    if (!r || !r.roomId || r.deleting === true || r.archived === true || !Array.isArray(r.tagCatalog)) continue;
    const adminSzoba = (Array.isArray(szobak) ? szobak : []).find((x) => x.roomId === r.roomId);
    if (jatszoterE(adminSzoba || r, [...(Array.isArray(szobak) ? szobak : []), ...(Array.isArray(elo) ? elo : [])])) continue;
    for (const c of r.tagCatalog) {
      if (!c || !c.id || uresE(c.name)) continue;
      const nevek = c.names && typeof c.names === "object" ? c.names : {};
      const hianyzo = kodok.filter((k) => uresE(nevek[k]));
      if (hianyzo.length > 0) kimenet.cimkek.push({ roomId: r.roomId, roomName: nevOf.get(r.roomId) || r.name || "", id: c.id, name: c.name, hianyzo });
    }
  }
  return kimenet;
}

/** A feladatok egy lapos listában: előbb a szobák, utána a címkék. */
export function feladatLista(hianyzo) {
  return [
    ...hianyzo.szobak.map((x) => ({ fajta: "szoba", roomId: x.roomId, nev: x.name, kodok: x.hianyzo })),
    ...hianyzo.cimkek.map((x) => ({ fajta: "cimke", roomId: x.roomId, cimkeId: x.id, nev: x.name, kodok: x.hianyzo })),
  ];
}

const alvas = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * A javaslatok elkészítése egyenként, a keretet tartva.
 * @param {Array} feladatok a feladatLista() eredménye
 * @param {(feladat) => Promise<Record<string,string>>} hivas a javaslat kérése (nyelvkód -> név)
 * @param {object} [opciok] { koz, varakozas, alvo, megszakitott: () => boolean, haladas: (n, osszes) => void }
 * @returns {Promise<{javaslatok: Array<{feladat, nevek}>, hibak: number, megszakadt: boolean, keretHiba: boolean, tulHosszu: number}>}
 *
 * Túlterheléskor (resource-exhausted) egyszer várunk és újrapróbáljuk; ha az sem megy, a futás ELŐRE megáll (keretHiba): a már elkészült
 * javaslatok megmaradnak, és újrafuttatáskor csak a még hiányzókat kérjük (hianyzoNevek újraszámolja).
 */
export async function javaslatokKeszitese(feladatok, hivas, opciok = {}) {
  const { koz = HIVAS_KOZ_MS, varakozas = TULTERHELES_VARAKOZAS_MS, alvo = alvas, megszakitott = () => false, haladas = () => {} } = opciok;
  const javaslatok = [];
  let hibak = 0;
  let keretHiba = false;
  let tulHosszu = 0; // v144: a modell adott fordítást, de hosszabb a megengedettnél (functions/out-of-range)
  for (let i = 0; i < feladatok.length; i++) {
    if (megszakitott()) return { javaslatok, hibak, megszakadt: true, keretHiba, tulHosszu };
    haladas(i, feladatok.length);
    let nevek = null;
    for (let proba = 0; proba < 2 && nevek === null; proba++) {
      try {
        nevek = (await hivas(feladatok[i])) || {};
      } catch (err) {
        const kod = String((err && err.code) || "");
        if (kod === "functions/resource-exhausted" && proba === 0) { await alvo(varakozas); continue; }
        if (kod === "functions/resource-exhausted") keretHiba = true;
        if (kod === "functions/out-of-range") tulHosszu++;
        break;
      }
    }
    if (keretHiba) return { javaslatok, hibak, megszakadt: false, keretHiba, tulHosszu };
    if (nevek === null) hibak++;
    else javaslatok.push({ feladat: feladatok[i], nevek });
    if (i < feladatok.length - 1) await alvo(koz);
  }
  haladas(feladatok.length, feladatok.length);
  return { javaslatok, hibak, megszakadt: false, keretHiba, tulHosszu };
}

/**
 * Mi kerül ténylegesen beírásra: csak a MÉG ÜRES nyelvekre, csak ismert nyelvekre, nem üres javaslat.
 * @param {Array} javaslatok a javaslatokKeszitese eredménye
 * @param {Array} szobak az AKTUÁLIS admin szobalista
 * @param {Array} elo az AKTUÁLIS élő szobalista (címkekatalógus)
 * @param {string[]} nyelvek
 * @returns {{szobak: Array<{roomId, name, parentRoomId, localizedNames, db}>, katalogusok: Array<{roomId, catalog, db}>, ossz: number}}
 */
export function mentesiTervek(javaslatok, szobak, elo, nyelvek) {
  const ismert = new Set(nyelvek);
  const tiszta = (nevek, mar) => {
    const ki = {};
    for (const [k, v] of Object.entries(nevek || {})) if (ismert.has(k) && uresE(mar[k]) && typeof v === "string" && v.trim()) ki[k] = v.trim();
    return ki;
  };
  const szobaTerv = new Map();
  const katalogusTerv = new Map();
  for (const j of javaslatok) {
    const f = j.feladat;
    if (f.fajta === "szoba") {
      const r = (szobak || []).find((x) => x.roomId === f.roomId);
      if (!r) continue;
      const mar = r.localizedNames || {};
      const uj = tiszta(j.nevek, mar);
      const db = Object.keys(uj).length;
      if (db === 0) continue;
      szobaTerv.set(f.roomId, { roomId: r.roomId, name: r.name, parentRoomId: r.parentRoomId || null, localizedNames: { ...mar, ...uj }, db });
    } else if (f.fajta === "cimke") {
      const szoba = (elo || []).find((x) => x.roomId === f.roomId);
      if (!szoba || !Array.isArray(szoba.tagCatalog)) continue;
      let terv = katalogusTerv.get(f.roomId);
      if (!terv) {
        terv = { roomId: f.roomId, catalog: szoba.tagCatalog.map((c) => ({ id: c.id, group: c.group, name: c.name, names: { ...(c.names || {}) } })), db: 0 };
        katalogusTerv.set(f.roomId, terv);
      }
      const elem = terv.catalog.find((c) => c.id === f.cimkeId);
      if (!elem) continue;
      const uj = tiszta(j.nevek, elem.names);
      const db = Object.keys(uj).length;
      if (db === 0) continue;
      elem.names = { ...elem.names, ...uj };
      terv.db += db;
    }
  }
  const szobak2 = [...szobaTerv.values()];
  const katalogusok = [...katalogusTerv.values()].filter((k) => k.db > 0);
  return { szobak: szobak2, katalogusok, ossz: szobak2.reduce((a, x) => a + x.db, 0) + katalogusok.reduce((a, x) => a + x.db, 0) };
}
